/**
 * Collection/search facet filtering via Shopify Section Rendering API (no full page reload).
 */
const FacetFilters = {
  filterData: [],
  searchParamsPrev: '',
  debounceTimer: null,

  init() {
    if (!this.getFilterForm()) return;

    this.sectionId = this.getSectionId();
    this.searchParamsPrev = window.location.search.slice(1);
    this.bindEvents();
  },

  getFilterForm() {
    return document.getElementById('FacetFiltersForm');
  },

  getSectionId() {
    const anchor =
      document.getElementById('ProductGridContainer') ||
      document.getElementById('FacetFiltersForm');

    const shopifySection = anchor?.closest('.shopify-section');
    if (shopifySection?.id) {
      return shopifySection.id.replace(/^shopify-section-/, '');
    }

    return (
      document.querySelector('.products_seciton[data-id]')?.dataset.id ||
      document.querySelector('.custom-collection-section[data-id]')?.dataset.id ||
      document.querySelector('.products_section[data-id]')?.dataset.id ||
      document.getElementById('product-grid-card')?.dataset.id
    );
  },

  isFilterForm(form) {
    return form?.id === 'FacetFiltersForm' || form?.id === 'FacetSortDrawerForm' || form?.id === 'FacetSortMobForm';
  },

  bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (this.isFilterForm(form)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    const onFacetControlEvent = (event) => {
      const target = event.target;
      if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'SELECT')) return;

      const isSortControl = target.id === 'SortBy' || target.name === 'sort_by';
      const form = target.closest?.('form');

      if (isSortControl && (!form || !this.isFilterForm(form))) {
        this.syncSortByInputs(target.value);
        this.debouncedOnFormChange(event);
        return;
      }

      if (!form || !this.isFilterForm(form)) return;

      if (isSortControl) {
        this.syncSortByInputs(target.value);
      }

      this.debouncedOnFormChange(event);
    };

    document.addEventListener('change', onFacetControlEvent, true);
    document.addEventListener('input', onFacetControlEvent, true);

    document.addEventListener('click', (event) => {
      const target = event.target.closest('.js-facet-remove');
      if (!target || target.classList.contains('disabled')) return;
      event.preventDefault();
      this.onActiveFilterClick(target);
    });

    window.addEventListener('popstate', (event) => {
      const searchParams = event.state?.searchParams ?? window.location.search.slice(1);
      if (searchParams === this.searchParamsPrev) return;
      this.renderPage(searchParams, null, false);
    });
  },

  debouncedOnFormChange(event) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.onFormChange(event), 300);
  },

  syncSortByInputs(sortValue) {
    document.querySelectorAll('[data-facet-sort-input]').forEach((input) => {
      input.value = sortValue;
    });
    const sortSelect = document.getElementById('SortBy');
    if (sortSelect && sortSelect.value !== sortValue) {
      sortSelect.value = sortValue;
    }
  },

  ensureSortByParam(params) {
    if (!params.has('sort_by')) {
      const sortSelect = document.getElementById('SortBy');
      const sortValue = sortSelect?.value || 'best-selling';
      params.set('sort_by', sortValue);
      this.syncSortByInputs(sortValue);
    }
  },

  getForms() {
    return ['FacetFiltersForm', 'FacetSortDrawerForm', 'FacetSortMobForm']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
  },

  normalizeFilterValue(key, value) {
    if (!key.includes('filter.v.price.')) return value;
    return String(value).trim().replace(',', '.');
  },

  buildSearchParams(event) {
    const merged = new URLSearchParams();
    const target = event?.target;
    const isMobile = target?.closest?.('form')?.id === 'FacetSortMobForm';

    this.getForms().forEach((form) => {
      const include = !isMobile
        ? form.id === 'FacetSortDrawerForm' || form.id === 'FacetFiltersForm'
        : form.id === 'FacetSortMobForm' || form.id === 'FacetFiltersForm';

      if (!include) return;

      for (const [key, value] of new FormData(form).entries()) {
        if (value === '') continue;
        merged.append(key, this.normalizeFilterValue(key, value));
      }
    });

    const sortSelect = document.getElementById('SortBy');
    if (sortSelect) {
      merged.delete('sort_by');
      merged.set('sort_by', sortSelect.value);
    }

    return merged.toString();
  },

  parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  },

  getFetchUrl(searchParamsString) {
    const params = new URLSearchParams(searchParamsString);

    if (!params.has('q')) {
      const q = new URLSearchParams(window.location.search).get('q');
      if (q) params.set('q', q);
    }

    this.ensureSortByParam(params);

    const displayParams = params.toString();

    const fetchUrl = new URL(window.location.pathname, window.location.origin);
    params.forEach((value, key) => fetchUrl.searchParams.append(key, value));

    if (this.sectionId) {
      fetchUrl.searchParams.set('section_id', this.sectionId);
    }

    return { fetchUrl: fetchUrl.toString(), displayParams };
  },

  onFormChange(event) {
    const searchParams = this.buildSearchParams(event);
    this.renderPage(searchParams, event, true);
  },

  onActiveFilterClick(target) {
    this.toggleActiveFacets(true);
    const searchString = target.href.includes('?') ? target.href.split('?')[1] : '';
    this.renderPage(searchString, null, true);
  },

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((el) => {
      el.classList.toggle('disabled', disable);
    });
  },

  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;

    const countContainer = document.getElementById('ProductCount');
    document.querySelectorAll('.products_inner_wrapper .loading__spinner').forEach((spinner) => {
      spinner.classList.remove('hidden');
    });

    const productGridContainer = document.getElementById('ProductGridContainer');
    const gridEl =
      productGridContainer?.querySelector('.collection') ||
      productGridContainer?.querySelector('.product_grid');
    if (gridEl) gridEl.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');

    const { fetchUrl, displayParams } = this.getFetchUrl(searchParams);
    const cacheHit = this.filterData.find((entry) => entry.url === fetchUrl);

    const finishLoading = () => {
      this.toggleActiveFacets(false);
      if (countContainer) countContainer.classList.remove('loading');
      document.querySelectorAll('.products_inner_wrapper .loading__spinner').forEach((spinner) => {
        spinner.classList.add('hidden');
      });
    };

    if (cacheHit) {
      this.renderFromHTML(cacheHit.html, event);
      finishLoading();
    } else {
      fetch(fetchUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`Facet fetch failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          this.filterData = [...this.filterData, { html, url: fetchUrl }];
          this.renderFromHTML(html, event);
        })
        .catch((err) => console.error('Facet filter fetch failed:', err))
        .finally(finishLoading);
    }

    if (updateURLHash) {
      const displayUrl = displayParams
        ? `${window.location.pathname}?${displayParams}`
        : window.location.pathname;
      history.pushState({ searchParams }, '', displayUrl);
    }
  },

  renderFromHTML(html, event) {
    const parsedHTML = this.parseHTML(html);

    this.renderFilters(parsedHTML, event);
    this.renderProductGridContainer(parsedHTML);
    this.renderProductCount(parsedHTML);
    this.renderActiveFacets(parsedHTML);
  },

  renderProductGridContainer(parsedHTML) {
    const newGrid = parsedHTML.getElementById('ProductGridContainer');
    const container = document.getElementById('ProductGridContainer');
    if (newGrid && container) {
      container.innerHTML = newGrid.innerHTML;
    }
  },

  renderProductCount(parsedHTML) {
    const countEl = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (countEl && container) {
      container.innerHTML = countEl.innerHTML;
    }
    if (countEl && containerDesktop) {
      containerDesktop.innerHTML = countEl.innerHTML;
    }

    const customCount = parsedHTML.querySelector('.collection-top-bar .product-count');
    const currentCustomCount = document.querySelector('.collection-top-bar .product-count');
    if (customCount && currentCustomCount) {
      currentCustomCount.innerHTML = customCount.innerHTML;
    }
  },

  renderFilters(parsedHTML, event) {
    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const jsFilter = event?.target?.closest?.('.js-filter');

    if (facetDetailsElements.length) {
      facetDetailsElements.forEach((element) => {
        if (jsFilter && element.dataset.index === jsFilter.dataset.index) return;
        const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
        if (target) target.innerHTML = element.innerHTML;
      });
    } else {
      const newForm = parsedHTML.getElementById('FacetFiltersForm');
      const currentForm = this.getFilterForm();
      if (newForm && currentForm) {
        currentForm.innerHTML = newForm.innerHTML;
      }
    }

    const sortInput = parsedHTML.querySelector('[data-facet-sort-input]');
    if (sortInput) this.syncSortByInputs(sortInput.value);

    if (typeof window.initFaqAccordions === 'function') {
      const filterRoot = document.querySelector('.filter_faq') || document.getElementById('FacetFiltersForm');
      window.initFaqAccordions(filterRoot);
    }

    if (typeof window.initPriceRangeSliders === 'function') {
      window.initPriceRangeSliders();
    }
  },

  renderActiveFacets(parsedHTML) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const newEl = parsedHTML.querySelector(selector);
      const current = document.querySelector(selector);
      if (current) current.innerHTML = newEl ? newEl.innerHTML : '';
    });
  },
};

function initFacetFilters() {
  FacetFilters.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFacetFilters);
} else {
  initFacetFilters();
}

document.addEventListener('shopify:section:load', initFacetFilters);

/* Price range sliders */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeMin = this.querySelector('.range-min');
    this.rangeMax = this.querySelector('.range-max');
    this.inputMin = this.querySelector('.input-min');
    this.inputMax = this.querySelector('.input-max');

    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax) return;

    const syncFromSlider = (rangeInput, numberInput) => {
      numberInput.value = rangeInput.value;
      numberInput.dispatchEvent(new Event('input', { bubbles: true }));
    };

    this.rangeMin.addEventListener('input', () => syncFromSlider(this.rangeMin, this.inputMin));
    this.rangeMax.addEventListener('input', () => syncFromSlider(this.rangeMax, this.inputMax));

    this.inputMin.addEventListener('input', () => {
      const value = parseFloat(this.inputMin.value);
      const maxValue = parseFloat(this.rangeMax.value);
      if (!Number.isNaN(value) && !Number.isNaN(maxValue) && value <= maxValue) {
        this.rangeMin.value = value;
        if (typeof updateRange === 'function') updateRange();
      }
    });

    this.inputMax.addEventListener('input', () => {
      const value = parseFloat(this.inputMax.value);
      const minValue = parseFloat(this.rangeMin.value);
      if (!Number.isNaN(value) && !Number.isNaN(minValue) && value >= minValue) {
        this.rangeMax.value = value;
        if (typeof updateRange === 'function') updateRange();
      }
    });
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}
